import { useEffect, useState } from 'react';
import { useBusiness } from '../contexts/BusinessContext';
import { reportMoney, reportMonthToDate, reportMonthWindow, runReport, type ReportResult } from '../lib/reports';

export interface MonthReportFigure {
  total: number;
  count: number;
  /** total divided by count (the report's average_sale), 0 when there are no sales. */
  average: number;
  formula: string;
  loading: boolean;
  error: string | null;
}

/** Scheduled sales for the month, split into what is ahead and what slipped. */
export interface ScheduledMonthFigure extends MonthReportFigure {
  /** Jobs dated today or later. */
  upcoming: { total: number; count: number };
  /** Jobs whose date has passed and are still not completed or invoiced. */
  pastDue: { total: number; count: number };
}

const emptyFigure: MonthReportFigure = {
  total: 0,
  count: 0,
  average: 0,
  formula: '',
  loading: true,
  error: null,
};

const emptyScheduled: ScheduledMonthFigure = {
  ...emptyFigure,
  upcoming: { total: 0, count: 0 },
  pastDue: { total: 0, count: 0 },
};

function toNumber(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function reportCount(result: ReportResult, metric: string): number {
  return toNumber(result.rows[0]?.[metric]);
}

function timingRow(result: ReportResult, timing: 'upcoming' | 'past_due') {
  const row = result.rows.find((r) => r.timing === timing);
  return { total: toNumber(row?.total_scheduled_sales), count: toNumber(row?.jobs) };
}

/** This-month figures from the sales and scheduled-sales reports. */
export function useMonthSalesReports() {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timeZone = selectedBusiness?.timezone || 'America/Vancouver';

  const [sales, setSales] = useState<MonthReportFigure>(emptyFigure);
  const [salesLastMonth, setSalesLastMonth] = useState(0);
  const [scheduled, setScheduled] = useState<ScheduledMonthFigure>(emptyScheduled);

  useEffect(() => {
    if (!businessId) {
      setSales({ ...emptyFigure, loading: false });
      setSalesLastMonth(0);
      setScheduled({ ...emptyScheduled, loading: false });
      return;
    }

    let cancelled = false;
    setSales((current) => ({ ...current, loading: true, error: null }));
    setScheduled((current) => ({ ...current, loading: true, error: null }));

    const current = reportMonthWindow(timeZone, 0);
    const currentToDate = reportMonthToDate(timeZone, 0);
    const previousToDate = reportMonthToDate(timeZone, -1);

    void runReport(
      businessId,
      `FROM sales SHOW total_sales, orders, average_sale SINCE ${currentToDate.since} UNTIL ${currentToDate.until}`,
    )
      .then((result) => {
        if (cancelled) return;
        setSales({
          total: reportMoney(result),
          count: reportCount(result, 'orders'),
          average: reportMoney(result, 'average_sale'),
          formula: result.formula,
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        console.error('Sales report:', err);
        if (cancelled) return;
        setSales({
          ...emptyFigure,
          loading: false,
          error: err instanceof Error ? err.message : 'Could not load sales',
        });
      });

    void runReport(
      businessId,
      `FROM sales SHOW total_sales SINCE ${previousToDate.since} UNTIL ${previousToDate.until}`,
    )
      .then((result) => {
        if (!cancelled) setSalesLastMonth(reportMoney(result));
      })
      .catch((err: unknown) => {
        console.error('Sales report last month:', err);
        if (!cancelled) setSalesLastMonth(0);
      });

    void runReport(
      businessId,
      `FROM scheduled_sales SHOW total_scheduled_sales, jobs GROUP BY timing SINCE ${current.since} UNTIL ${current.until}`,
    )
      .then((result) => {
        if (cancelled) return;
        const upcoming = timingRow(result, 'upcoming');
        const pastDue = timingRow(result, 'past_due');
        const count = upcoming.count + pastDue.count;
        const total = upcoming.total + pastDue.total;
        setScheduled({
          total,
          count,
          average: count > 0 ? total / count : 0,
          upcoming,
          pastDue,
          formula: result.formula,
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        console.error('Scheduled sales report:', err);
        if (cancelled) return;
        setScheduled({
          ...emptyScheduled,
          loading: false,
          error: err instanceof Error ? err.message : 'Could not load scheduled sales',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [businessId, timeZone]);

  return { sales, salesLastMonth, scheduled };
}
